import React, { useState } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import { COLORS } from "../../shared/colors";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import ROUTES from "../../routes";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { config } from "../../config";
import axios from "axios";

const formSchemaOrg = yup.object().shape({
	name: yup.string().required("Name is required"),
	email: yup.string().required("Email is required").email("Email is invalid"),
	password: yup
		.string()
		.required("Password is required")
		.min(7, "Password must be at least 7 characters")
		.max(15, "Password cannot exceed more than 15 characters"),
	confirmPass: yup
		.string()
		.oneOf([yup.ref("password"), null], "Passwords must match")
		.required("Confirm password is required"),
	companyName: yup.string().required("Company name is required"),
	phone: yup.string().required("Phone is required"),
	address1: yup.string().required("Address Line 1 is required"),
	cityStateZip: yup.string().required("City, State & Zip is required"),
	mcNumber: yup.string().when("orgType", {
		is: (value) => value !== "dispatcher",
		then: () => yup.string().required("MC Number is required"),
	}),
	dot: yup.string().when("orgType", {
		is: (value) => value !== "dispatcher",
		then: () => yup.string().required(),
	}),
	taxId: yup.string(),
	accountType: yup.string().required("Account Type is required"),
	orgType: yup.string().required("Type is required"),
	depositType: yup.string().required("Deposit Type is required"),
	abaRoutingNumber: yup.string().required("ABA Routing Number is required"),
	accountNumber: yup.string().required("Account Number is required"),
});

const RegisterOrg = () => {
	const {
		reset,
		register,
		handleSubmit,
		watch,
		control,
		formState: { errors },
		getValues,
	} = useForm({
		resolver: yupResolver(formSchemaOrg),
		defaultValues: {
			orgType: "carrier",
		},
	});

	const [disableSubmit, setDisableSubmit] = useState(false);
	const navigate = useNavigate();
	const orgType = watch("orgType");

	const validatePasswordMatch = (value) => {
		const password = getValues("password");
		return value === password || "Passwords do not match";
	};

	const onSubmit = async (data) => {
		setDisableSubmit(true);
		try {
			const formData = new FormData();
			if (data.logo && data.logo instanceof File) {
				formData.append("logo", data.logo);
			}

			if (data.voidedCheck && data.voidedCheck instanceof File) {
				formData.append("voidedCheck", data.voidedCheck);
			}

			Object.keys(data).forEach((key) => {
				if (key !== "logo" && key !== "voidedCheck") {
					formData.append(key, data[key]);
				}
			});

			formData.append("type", "org");

			try {
				const response = await axios({
					method: "post",
					url: `${config.apiBaseUrl}/signup?type=org&email=${data.email}`,
					data: formData,
				});
				if (response.data && !response.error) {
					toast("Welcome Aboard! Account Created Successfully!");
					navigate(ROUTES.PATHS.LOGIN);
					setDisableSubmit(false);
				} else {
					console.error("SignUp failed:", response.error);
					toast("Failed to sign Up!");
					setDisableSubmit(false);
				}
			} catch (error) {
				console.error("Error:", error);
				throw error;
			}
		} catch (error) {
			// Handle any errors that occur during the update process
			console.error("An error occurred:", error);
			toast(
				error.response && error.response.data && error.response.data.error
					? error.response.data.error
					: "An unexpected error occurred while sign up!"
			);
			setDisableSubmit(false);
		}
	};

	return (
		<div
			style={{
				backgroundColor: COLORS.CULTURED,
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
			}}
		>
			<Box mb={3} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
				<img
					// eslint-disable-next-line no-undef
					src={require("../../assets/images/logo.png")}
					style={{
						width: "180px",
						height: "auto",
					}}
				/>
			</Box>
			<Card
				style={{
					border: "0.0625rem solid  #d1d7e0",
					boxShadow: "0 2px 18px rgba(0, 0, 0, 0.02)",
					borderRadius: " 0.5rem",
					width: "80%",
					margin: "0 auto",
				}}
				elevation={0}
			>
				<CardContent sx={{ minWidth: 378, padding: "30px 48px" }}>
					<Box mb={2}>
						<Typography sx={{ fontSize: 28 }} align="center" color={COLORS.SECONDARY}>
							Register
						</Typography>
						<Typography sx={{ fontSize: 16, color: COLORS.INDEPENDENCE }} align="center">
							Start Your Journey with Us
						</Typography>
					</Box>
					<form onSubmit={handleSubmit(onSubmit)}>
						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridGap: "10px" }}>
							<FormControl fullWidth margin="normal" error={!!errors.orgType}>
								<InputLabel>Type</InputLabel>
								<Controller
									name="orgType"
									control={control}
									defaultValue="carrier"
									render={({ field }) => (
										<Select size="small" {...field} label="Type">
											<MenuItem value="dispatcher">Dispatcher</MenuItem>
											<MenuItem value="carrier">Carrier</MenuItem>
										</Select>
									)}
								/>
								{errors.orgType && <FormHelperText>{errors.orgType.message}</FormHelperText>}
							</FormControl>
							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="name"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<TextField
											{...field}
											error={errors.name}
											id="filled-hidden-label-small"
											size="small"
											label="Name"
											helperText={errors.name?.message}
											InputProps={{ disableUnderline: true }}
										/>
									)}
								/>
							</FormControl>

							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="email"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<TextField
											{...field}
											error={errors.email}
											id="filled-hidden-label-small"
											size="small"
											label="Email"
											helperText={errors.email?.message}
											InputProps={{ disableUnderline: true }}
										/>
									)}
								/>
							</FormControl>

							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="password"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<TextField
											{...field}
											error={errors.password}
											id="password"
											type="password"
											size="small"
											label="Password"
											helperText={errors.password?.message}
											InputProps={{ disableUnderline: true }}
										/>
									)}
								/>
							</FormControl>

							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="confirmPass"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<TextField
											{...field}
											{...register("confirmPass", {
												required: true,
												validate: validatePasswordMatch,
											})}
											error={errors.confirmPass}
											id="confirmPass"
											type="password"
											label={"Confirm Password"}
											size="small"
											helperText={errors.confirmPass && "Please confirm your password"}
											InputProps={{ disableUnderline: true }}
										/>
									)}
								/>
							</FormControl>

							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="companyName"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<TextField
											{...field}
											required
											label="Company Name"
											size="small"
											type="text"
											error={!!errors.companyName}
											helperText={errors.companyName?.message}
										/>
									)}
								/>
							</FormControl>

							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="phone"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<TextField
											{...field}
											required
											label="Phone"
											size="small"
											type="text"
											error={!!errors.phone}
											helperText={errors.phone?.message}
										/>
									)}
								/>
							</FormControl>

							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="address1"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<TextField
											{...field}
											required
											label="Address Line 1"
											size="small"
											type="text"
											error={!!errors.address1}
											helperText={errors.address1?.message}
										/>
									)}
								/>
							</FormControl>
							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="cityStateZip"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<TextField
											{...field}
											required
											label="City, State & Zip"
											size="small"
											type="text"
											error={!!errors.cityStateZip}
											helperText={errors.cityStateZip?.message}
										/>
									)}
								/>
							</FormControl>
							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="taxId"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											label="Tax ID"
											size="small"
											type="text"
											error={!!errors.taxId}
										/>
									)}
								/>
							</FormControl>

							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="accountType"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											label="Account Type"
											type="text"
											size="small"
											error={!!errors.accountType}
											helperText={errors.accountType?.message}
										/>
									)}
								/>
							</FormControl>

							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="depositType"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											label="Deposit Type"
											type="text"
											size="small"
											error={!!errors.depositType}
											helperText={errors.depositType?.message}
										/>
									)}
								/>
							</FormControl>

							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="abaRoutingNumber"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											label="ABA Routing Number"
											type="text"
											size="small"
											error={!!errors.abaRoutingNumber}
											helperText={errors.abaRoutingNumber?.message}
										/>
									)}
								/>
							</FormControl>

							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="accountNumber"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											label="Account Number"
											type="text"
											size="small"
											error={!!errors.accountNumber}
											helperText={errors.accountNumber?.message}
										/>
									)}
								/>
							</FormControl>

							{/* Conditionally render MC# and DOT based on type not being 'dispatcher' */}
							{orgType !== "dispatcher" && (
								<>
									<FormControl style={{ margin: "16px 0px" }}>
										<Controller
											name="mcNumber"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="MC#"
													type="text"
													size="small"
													error={!!errors.mcNumber}
													helperText={errors.mcNumber?.message}
												/>
											)}
										/>
									</FormControl>
									<FormControl style={{ margin: "16px 0px" }}>
										<Controller
											name="dot"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="DOT"
													type="text"
													size="small"
													error={!!errors.dot}
													helperText={errors.dot?.message}
												/>
											)}
										/>
									</FormControl>
								</>
							)}
							<FormControl sx={{ alignSelf: "center" }}>
								<Controller
									name="logo"
									control={control}
									defaultValue={null}
									render={({ field: { onChange, onBlur, name, ref } }) => (
										<TextField
											type="file"
											onChange={(e) => {
												const file = e.target.files[0];
												if (file) {
													onChange(file);
												}
											}}
											onBlur={onBlur}
											name={name}
											inputRef={ref}
											InputLabelProps={{ shrink: true }}
											size="small"
											label="Logo"
											variant="outlined"
										/>
									)}
								/>
							</FormControl>
							<FormControl sx={{ alignSelf: "center" }}>
								<Controller
									name="voidedCheck"
									control={control}
									defaultValue={null}
									render={({ field: { onChange, onBlur, name, ref } }) => (
										<TextField
											type="file"
											onChange={(e) => {
												const file = e.target.files[0];
												if (file) {
													onChange(file);
												}
											}}
											onBlur={onBlur}
											name={name}
											inputRef={ref}
											InputLabelProps={{ shrink: true }}
											size="small"
											label="Voided Check"
											variant="outlined"
										/>
									)}
								/>
							</FormControl>
						</div>

						<Box mt={2.5} mb={2}>
							<Button
								variant="contained"
								style={{ backgroundColor: COLORS.SECONDARY }}
								fullWidth
								type="submit"
								disabled={disableSubmit}
							>
								Sign Up
							</Button>
						</Box>
					</form>
					<Box mt={2.5} mb={2} sx={{ textAlign: "center" }}>
						Already have an account? <Link to="/login">Log in</Link>
					</Box>
				</CardContent>
			</Card>
		</div>
	);
};

export default RegisterOrg;
